import { Component, inject, HostListener, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogRef, DIALOG_DATA } from '@angular/cdk/dialog';
import { ScoButtonModule, ScoDialogModule, ScoSelectModule } from '@schueco/schueco-ui-components';
import { Store } from '@ngrx/store';
import { selectAllCountriesAndRoles } from '@frontend/data-access-land-role';
import { FormsModule } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { LAND_ROLE_VIEW_KEY, LandRoleView } from '@frontend/data-access-land-role';
import { setUserSettings, setUserSettingsSuccess, setUserSettingsFailure } from '@frontend/data-access-user-settings';
import { Actions, ofType } from '@ngrx/effects';
import { filter, take } from 'rxjs/operators';
import { setUserInfo } from '@frontend/data-access-user-info';

@Component({
  selector: 'dc-land-role-dialog',
  imports: [
    CommonModule,
    ScoButtonModule,
    ScoDialogModule,
    FormsModule,
    MatFormFieldModule,
    MatSelectModule,
    ScoSelectModule,
    TranslateModule,
  ],
  templateUrl: './land-role-dialog.component.html',
  styleUrl: './land-role-dialog.component.css',
})
export class LandRoleDialogComponent implements OnInit, OnDestroy {
  private store = inject(Store);
  private dialogData = inject<{
    preselectedCountryId?: string | null;
    preselectedRoleId?: string | null;
  }>(DIALOG_DATA, { optional: true });

  actions$ = inject(Actions);
  dialogRef = inject<DialogRef>(DialogRef);
  landRole$: Observable<{ countries: { id: string; name: string }[]; roles: { id: string; name: string }[] }>;

  selectedCountryId: string | null = null;
  selectedRoleId: string | null = null;

  countries: { id: string; name: string }[] = [];
  roles: { id: string; name: string }[] = [];

  private landRoleSubscription!: Subscription;
  isMobileView = false;
  isSetViewActive = false;

  constructor() {
    this.landRole$ = this.store.select(selectAllCountriesAndRoles);
  }

  @HostListener('window:resize', [])
  onResize() {
    this.checkScreenSizeAfterResize();
  }

  ngOnInit() {
    this.landRoleSubscription = this.store.select(selectAllCountriesAndRoles).subscribe((data) => {
      if (!data || !data.countries || !data.roles) {
        return;
      }

      // Check if preselected values were provided via dialog data
      if (this.dialogData?.preselectedCountryId || this.dialogData?.preselectedRoleId) {
        const validCountryId = data.countries?.some((c) => c.id === this.dialogData?.preselectedCountryId)
          ? this.dialogData.preselectedCountryId
          : null;
        const validRoleId = data.roles?.some((r) => r.id === this.dialogData?.preselectedRoleId)
          ? this.dialogData.preselectedRoleId
          : null;

        this.selectedCountryId = validCountryId ?? null;
        this.selectedRoleId = validRoleId ?? null;
      } else {
        // Fallback to localStorage
        const stored = localStorage.getItem(LAND_ROLE_VIEW_KEY);
        let parsed: LandRoleView = { countryId: null, roleId: null };

        if (stored) {
          try {
            parsed = JSON.parse(stored);
          } catch (e) {
            console.warn('Invalid landRoleView JSON:', e);
          }
        }

        const validCountryId = data.countries?.some((c) => c.id === parsed.countryId) ? parsed.countryId : null;
        const validRoleId = data.roles?.some((r) => r.id === parsed.roleId) ? parsed.roleId : null;

        this.selectedCountryId = validCountryId;
        this.selectedRoleId = validRoleId;
      }
    });
    this.checkScreenSize();
  }

  ngOnDestroy() {
    if (this.landRoleSubscription) {
      this.landRoleSubscription.unsubscribe();
    }
  }

  setView() {
    const payload: LandRoleView = {
      countryId: this.selectedCountryId,
      roleId: this.selectedRoleId,
    };

    localStorage.setItem(LAND_ROLE_VIEW_KEY, JSON.stringify(payload));

    // Helper-Funktion, die auf Success oder Failure für einen Key wartet
    const waitForSet = (key: string) =>
      new Promise((resolve) => {
        const sub = this.store.select(() => null).subscribe(); // Dummy, damit unsubscribe möglich
        const actions$ = (this as any).actions$ || (window as any).actions$;
        if (!actions$) {
          resolve(undefined);
          return;
        }
        const subscription = actions$
          .pipe(
            (window as any).ngrxEffectsOfType
              ? (window as any).ngrxEffectsOfType(setUserSettingsSuccess, setUserSettingsFailure)
              : ofType(setUserSettingsSuccess, setUserSettingsFailure),
            filter((action: any) => {
              if (
                action.type === setUserSettingsSuccess.type &&
                (action.response?.key === key || action.response === key)
              )
                return true;
              if (action.type === setUserSettingsFailure.type && (action.error?.key === key || action.key === key))
                return true;
              return false;
            }),
            take(1)
          )
          .subscribe((result: any) => {
            subscription.unsubscribe();
            sub.unsubscribe();
            resolve(result);
          });
      });

    (async () => {
      this.isSetViewActive = true;
      if (payload.countryId) {
        this.store.dispatch(setUserSettings({ key: 'country', value: payload.countryId }));
        await waitForSet('country');
      }
      if (payload.roleId) {
        this.store.dispatch(setUserSettings({ key: 'role', value: payload.roleId }));
        await waitForSet('role');
      }
      this.store.dispatch(setUserInfo({ isSet: true }));
      this.isSetViewActive = false;
      this.dialogRef.close();
      window.location.reload();
    })();
  }

  cancelDialog() {
    this.dialogRef.close();
  }

  transformToSelectItems(items: { id: string; name: string }[]): { id: string; name: string; value: string }[] {
    // Filter out null/undefined items and items with null/undefined properties
    return items?.filter((item) => item && item.id && item.name).map((item) => ({ ...item, value: item.id })) ?? [];
  }

  private checkScreenSize() {
    this.isMobileView = window.innerWidth <= 768;
  }

  private checkScreenSizeAfterResize() {
    if (window.innerWidth <= 768) {
      this.dialogRef.close();
      this.isMobileView = true;
    } else {
      this.isMobileView = false;
    }
  }
}
